import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Headphones, TrendingUp, Users, Clock, CheckCircle, Award } from 'lucide-react';
import LandingNav from '../../components/Landing-Nav';
import FooterLanding from '../../components/FooterLanding';

const CallCentersIndustry = () => {
  const features = [
    { icon: Clock, title: 'Queue Management', description: 'Match agent availability to call volume in real-time' },
    { icon: TrendingUp, title: 'Performance Metrics', description: 'Track KPIs and optimize staffing decisions' },
    { icon: Users, title: 'Shift Bidding', description: 'Empower agents to choose preferred shifts' },
    { icon: Headphones, title: 'Skills-Based Routing', description: 'Schedule agents based on expertise and certifications' }
  ];

  const benefits = [
    '50% faster schedule creation',
    '32% decrease in idle time',
    '94% customer satisfaction',
    'Real-time call volume forecasting',
    'Automated break scheduling',
    'Agent skill tracking'
  ];

  const stats = [
    { value: '150+', label: 'Call Centers' },
    { value: '50%', label: 'Faster Scheduling' },
    { value: '94%', label: 'Customer Satisfaction' },
    { value: '32%', label: 'Idle Time Reduction' }
  ];

  return (
    <>
      <LandingNav />
      
      <section className="pt-24 pb-16 bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="flex items-center space-x-2 mb-4">
                <Headphones className="w-8 h-8" />
                <span className="text-cyan-200 font-semibold">Call Centers & Customer Support</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Maximize Agent Productivity with Intelligent Scheduling
              </h1>
              <p className="text-xl text-cyan-100 mb-8">
                Optimize call center operations with AI-powered workforce management that matches staffing to call volume patterns.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup" className="px-8 py-4 bg-white text-cyan-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all text-center">
                  Schedule a Demo
                </Link>
                <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all text-center">
                  View Pricing
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <img src="/AI page.png" alt="Call Center Dashboard" className="rounded-2xl shadow-2xl border-4 border-white/20" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-cyan-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Purpose-Built for Contact Centers</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Reduce idle time and improve service levels with smart scheduling</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}>
              <img src="/Newsfeed.png" alt="Performance Dashboard" className="rounded-2xl shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Proven Results for Call Centers</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-16 h-16 text-cyan-600 mx-auto mb-6" />
          <blockquote className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            "With Shiftry, we cut schedule creation time in half and our customer satisfaction scores improved dramatically."
          </blockquote>
          <p className="text-lg text-gray-600">- Jennifer Rodriguez, Operations Director, TechSupport Solutions</p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-cyan-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Transform Your Call Center Operations</h2>
          <p className="text-xl text-cyan-100 mb-8">Join 150+ successful contact centers optimizing their workforce</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-4 bg-white text-cyan-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
              Schedule Your Free Demo
            </Link>
            <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all">
              View Pricing Plans
            </Link>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default CallCentersIndustry;
